import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  useTheme,
  useMediaQuery,
  styled,
} from '@mui/material';

// Styled component for the main container
const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#f4f6f8',
  overflow: 'hidden',
  position: 'relative',
}));

// Styled component for the gradient text
const GradientText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  fontWeight: 800,
  display: 'inline-block',
}));

// Styled component for the original BOTTOM diagonal banner (with text)
const DiagonalBanner = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: '-10%',
  width: '120%',
  marginBottom:"13vh",
  transform: 'rotate(-4deg)',
  transformOrigin: 'bottom left',
  padding: theme.spacing(1.5, 0),
  zIndex: 10,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, rgba(192, 132, 252, 0.15) 0%, rgba(236, 72, 153, 0.15) 50%, rgba(129, 140, 248, 0.15) 100%)`,
    zIndex: -1,
  },
}));

// Styled component for the BOTTOM intersecting diagonal line
const IntersectingDiagonalLine = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: '-10%',
  marginBottom:"13vh",
  width: '120%',
  transform: 'rotate(4.5deg)',
  transformOrigin: 'bottom right',
  padding: theme.spacing(3, 0),
  zIndex: 9,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, rgba(255, 64, 226, 0) 15.87%, rgba(0, 100, 255, 0.06) 64.9%, rgba(0, 100, 255, 0.08) 100%)`,
    zIndex: -1,
  },
}));

// --- STYLED COMPONENTS FOR THE TOP ---

// New Styled component for the TOP diagonal banner (with text)
const TopDiagonalBanner = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 100, // Adjusted: Positioned at the very top of the viewport
  left: '-10%',
  width: '120%',
  transform: 'rotate(-4deg)',
  transformOrigin: 'top left',
  padding: theme.spacing(1.5, 0),
  zIndex: 10,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, rgba(192, 132, 252, 0.15) 0%, rgba(236, 72, 153, 0.15) 50%, rgba(129, 140, 248, 0.15) 100%)`,
    zIndex: -1,
  },
}));

// New Styled component for the TOP intersecting diagonal line
const TopIntersectingDiagonalLine = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 100, // Adjusted: Positioned at the very top of the viewport
  left: '-10%',
  width: '120%',
  transform: 'rotate(4.5deg)',
  transformOrigin: 'top right',
  padding: theme.spacing(3, 0),
  zIndex: 9,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(90deg, rgba(255, 64, 226, 0) 15.87%, rgba(0, 100, 255, 0.06) 64.9%, rgba(0, 100, 255, 0.08) 100%)`,
    zIndex: -1,
  },
}));

// Styled component for the banner text container (reused)
const BannerTextContainer = styled(Box)({
  display: 'flex',
  width: 'fit-content',
});

const BannerIcon = styled('img')(({ theme }) => ({
  width: '20px',
  height: '20px',
  margin: theme.spacing(0, 0.5),
  verticalAlign: 'middle',
  opacity: 0.8,
}));

// Styled component for individual banner text items (reused)
const BannerTextItem = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.light})`,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  fontWeight: 600,
  fontSize: '1.5rem',
  padding: theme.spacing(0, 4),
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  textTransform: 'lowercase',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
    padding: theme.spacing(0, 2),
  },
}));

const ComingSoonPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const bannerContent = Array(10).fill("coming soon");

  return (
    <MainContainer sx={{ width: '100%', height: '100%' }}>
      {/* --- TOP BANNERS --- */}
      {/* <TopIntersectingDiagonalLine /> */}
      {/* <TopDiagonalBanner>
        <BannerTextContainer>
          {bannerContent.map((text, index) => (
            <React.Fragment key={`top-banner-${index}`}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BannerTextItem>
                  {text}
                </BannerTextItem>
                <BannerIcon src="/assets/coming-soon-stars-tilted.svg" alt="tilted icon" />
              </Box>
            </React.Fragment>
          ))}
        </BannerTextContainer>
      </TopDiagonalBanner> */}
      {/* --- END OF TOP BANNERS --- */}

      <Grid container sx={{ flexGrow: 1, alignItems: 'flex-start', p: { xs: 2, sm: 4, md: 6 }, zIndex: 1 }}>
        {/* Left Text Content */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', textAlign: { xs: 'center', md: 'left' }, mb: { xs: 6, md: 0 } ,mt:15}}>
          <Chip
            icon={
              <img
                src="/assets/coming-soon-stars.svg"
                alt="stars"
                style={{
                  width: '16px',
                  height: '16px',
                  color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[700],
                }}
              />
            }
            label="Stay Tuned"
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[100],
              border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]}`,
              color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[700],
              fontWeight: 500,
              mb: 2,
              fontSize: '0.75rem',
              height: '28px',
              width: '130px',
              borderRadius: '16px',
              paddingLeft: theme.spacing(1),
              paddingRight: theme.spacing(1),
              flexShrink: 0,
              alignSelf: { xs: 'center', md: 'flex-start' },
              '.MuiChip-icon': {
                marginRight: theme.spacing(0.5),
                marginLeft: theme.spacing(-0.5),
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
          <GradientText variant={isMobile ? "h2" : "h1"} >
            Creating
          </GradientText>
          <Typography variant={isMobile ? "h4" : "h3"} component="h2" fontWeight="bold" sx={{ mt: 1, color: theme.palette.text.primary }}>
            Something Amazing...
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, maxWidth: { xs: '100%', md: 480 }, mx: { xs: 'auto', md: 0 } }}>
            We're working hard to bring this page to you. It will be available soon.
          </Typography>
        </Grid>

        {/* Right Image Content */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: 300, sm: 400, md: 500 },
              height: { xs: 200, sm: 300, md: 350 },
              backgroundImage: "url('/assets/coming-soon-component.svg')",
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          />
        </Grid>
      </Grid>

      {/* Bottom Intersecting Decorative Diagonal Line */}
      <IntersectingDiagonalLine />

      {/* Bottom Original Diagonal Banner (with text) */}
      <DiagonalBanner>
        <BannerTextContainer>
          {bannerContent.map((text, index) => (
            <React.Fragment key={`bottom-banner-${index}`}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BannerTextItem>
                  {text}
                </BannerTextItem>
                <BannerIcon src="/assets/coming-soon-stars-tilted.svg" alt="tilted icon" />
              </Box>
            </React.Fragment>
          ))}
        </BannerTextContainer>
      </DiagonalBanner>
    </MainContainer>
  );
};

export default ComingSoonPage;